"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { OptimizedImage } from "@/components/common/OptimizedImage";
import { useAppSelector } from "../../_hooks/hooks";
import { ICMS } from "../home/homeModel";
import { X, Image as ImageIcon } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { Preloader } from "@/components/Preloader";
import { createLookupObject } from "@/_helper/helper";
import { usePathname } from "next/navigation";

interface GalleryProps {
  cmsData?: any[];
  PropertyWithGalleyList: any;
}

export default function Gallery({
  cmsData,
  PropertyWithGalleyList,
}: GalleryProps) {
  const pathname = usePathname();
  const path = pathname?.split("/")?.[1];
  const loading = useAppSelector((state) => state.property.loading);
  const BannerList = useAppSelector((state) => state.home.BannerList);

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const [activePropertyId, setActivePropertyId] = useState<number | null>(null);

  // Swiper state
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);

  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === path) || {};

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // If a property has no gallery images, filter it or just show its banner image
  const propertiesWithImages =
    PropertyWithGalleyList?.filter(
      (p: any) => p?.GalleryImages && p?.GalleryImages?.length > 0,
    ) || [];

  const handlePropertyClick = (propertyId: number) => {
    setActivePropertyId(activePropertyId === propertyId ? null : propertyId);
  };

  useEffect(() => {
    if (activePropertyId === null && propertiesWithImages?.length > 0) {
      setActivePropertyId(propertiesWithImages[0]?.ID);
    }
  }, [propertiesWithImages]);

  const openImageModal = (propertyId: number, imageIndex: number) => {
    setActivePropertyId(propertyId);
    setInitialSlideIndex(imageIndex);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Optional: reset swiper states when closing
    setThumbsSwiper(null);
  };

  const activeProperty = PropertyWithGalleyList?.find(
    (p: any) => p?.ID === activePropertyId,
  );

  return (
    <>
      <main className="min-h-screen bg-[#F9F7F4] text-md">
        {loading && <Preloader />}

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <OptimizedImage
              src={banner?.BannerImage}
              alt={banner?.BannerTitle || "Our Gallery"}
              priority={true}
              sizes="100vw"
              className="w-full h-full"
              aspectRatio="auto"
              objectFit="cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 mt-25 mb-15">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {banner?.SubTitle && (
                <div className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
                  <p className="text-[#C9A962] tracking-[0.4em] text-sm">
                    {banner?.SubTitle}
                  </p>
                </div>
              )}

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight font-playfair">
                {/* Let's Start a<br /> */}
                <span className="text-[#C9A962]">
                  {banner?.BannerTitle || "Our Gallery"}
                </span>
              </h1>

              {banner?.Description && (
                <p
                  className="text-white/95 text-xl md:text-2xl max-w-3xl mx-auto font-playfair font-playfair"
                  dangerouslySetInnerHTML={{
                    __html: banner?.Description,
                  }}
                />
              )}
            </motion.div>
          </div>
        </section>

        {/* Property List Section */}
        <section className="py-20 relative z-20 -mt-10 bg-transparent">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {lookUpCMSData?.["Gallery - Section 1 : Title"] ? (
              <div
                className="text-center mb-16"
                dangerouslySetInnerHTML={{
                  __html:
                    lookUpCMSData?.["Gallery - Section 1 : Title"]?.Template,
                }}
              />
            ) : (
              <div className="text-center mb-16">
                <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                  Visual Tour
                </p>
                <h2 className="text-2xl md:text-4xl mb-6 text-[#1A1A1A]">
                  Property Gallery
                </h2>
                <div className="w-24 h-1 bg-[#C9A962] mx-auto mb-8" />
                <p className="text-gray-600 text-xl max-w-2xl mx-auto">
                  Select a property to view its collection of stunning images.
                </p>
              </div>
            )}

            {/* Property Cards */}
            <div
              className={
                `grid grid-cols-1` +
                (propertiesWithImages?.length > 3
                  ? "md:grid-cols-2 lg:grid-cols-4 gap-8"
                  : propertiesWithImages?.length > 2
                    ? "md:grid-cols-2 lg:grid-cols-3 gap-8"
                    : "md:grid-cols-2 gap-8")
              }
            >
              {propertiesWithImages.map((property: any, index: number) => (
                <motion.div
                  key={property.ID}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group relative cursor-pointer rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex items-center justify-between p-6 transition-all duration-300 ${activePropertyId === property.ID ? "bg-[#C9A962] text-white" : "bg-gray-50 text-gray-400 group-hover:bg-[#C9A962]/10 group-hover:text-[#C9A962]"}`}
                  onClick={() => handlePropertyClick(property.ID)}
                >
                  <div className="flex-1">
                    <p
                      className={`text-xs font-medium tracking-widest uppercase mb-2 ${activePropertyId === property.ID ? "text-[#B42025]" : "text-[#C9A962]"}`}
                    >
                      {property.GalleryImages?.length || 0} Photos
                    </p>
                    <h3
                      className={`text-md font-medium text-gray-900 group-hover:text-[#B42025] transition-colors ${activePropertyId === property.ID ? "!text-white" : ""}`}
                    >
                      {property.PropertyName}
                    </h3>
                  </div>
                  {/* <div
                    className={`p-3 rounded-full transition-colors ${activePropertyId === property.ID ? "bg-[#C9A962] text-white" : "bg-gray-50 text-gray-400 group-hover:bg-[#C9A962]/10 group-hover:text-[#C9A962]"}`}
                  >
                    <ImageIcon className="w-6 h-6" />
                  </div> */}
                </motion.div>
              ))}
            </div>

            {/* Expanded Gallery Grid for Selected Property */}
            <AnimatePresence mode="wait">
              {activePropertyId && activeProperty && (
                <motion.div
                  key={activeProperty.ID}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-16 bg-white rounded-3xl p-8 shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-2xl font-medium font-light text-gray-900 border-l-4 border-[#C9A962] pl-4">
                      {activeProperty.PropertyName} Gallery
                    </h3>
                    {/* <button
                      onClick={() => setActivePropertyId(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-6 h-6 text-gray-500" />
                    </button> */}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {activeProperty?.GalleryImages?.map(
                      (img: any, idx: number) => (
                        <div
                          key={img.ID}
                          // initial={{ opacity: 0, scale: 0.9 }}
                          // animate={{ opacity: 1, scale: 1 }}
                          // transition={{ delay: idx * 0.05 }}
                          className="relative aspect-[16/11] rounded-xl overflow-hidden cursor-pointer group"
                          onClick={() =>
                            openImageModal(activeProperty?.ID, idx)
                          }
                        >
                          <OptimizedImage
                            src={img.MainGalleryImages}
                            alt={img.PropertyID || `Gallery Image ${idx + 1}`}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            className="w-full h-full"
                            imageClassName="transition-transform duration-500 group-hover:scale-110"
                            aspectRatio="16/11"
                            isObjectFit={false}
                            priority={idx < 6}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>

      {/* Fullscreen Swiper Modal */}
      <AnimatePresence>
        {isModalOpen && activeProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 text-white absolute top-0 left-0 right-0 z-10">
              <div className="max-w-[70%]">
                <h3 className="text-2xl font-light">
                  {activeProperty.PropertyName}
                </h3>
              </div>
              <button
                onClick={closeModal}
                className="bg-white/10 hover:bg-white/20 p-3 rounded-full backdrop-blur-md transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Swiper Container */}
            <div className="flex-1 flex flex-col justify-center items-center p-6 mt-16 w-full max-w-7xl mx-auto overflow-hidden">
              {/* Main Slider */}
              <div className="w-full h-[60vh] md:h-[70vh] mb-4">
                <Swiper
                  style={
                    {
                      "--swiper-navigation-color": "#fff",
                      "--swiper-pagination-color": "#fff",
                    } as any
                  }
                  loop={true}
                  spaceBetween={10}
                  navigation={true}
                  thumbs={{
                    swiper:
                      thumbsSwiper && !thumbsSwiper.destroyed
                        ? thumbsSwiper
                        : null,
                  }}
                  modules={[FreeMode, Navigation, Thumbs]}
                  initialSlide={initialSlideIndex}
                  className="w-full h-full rounded-2xl overflow-hidden"
                  watchSlidesProgress={true}
                >
                  {activeProperty.GalleryImages?.map(
                    (img: any, idx: number) => (
                      <SwiperSlide
                        key={`main-${idx}`}
                        className="flex justify-center items-center bg-transparent relative w-full h-full"
                      >
                        <OptimizedImage
                          src={img.MainGalleryImages}
                          alt={img.PropertyID || "Gallery image"}
                          sizes="100vw"
                          className="w-full h-[60vh] md:h-[70vh]"
                          aspectRatio="auto"
                          objectFit="contain"
                          style={{ backgroundColor: "transparent" }}
                          priority={idx === initialSlideIndex}
                        />
                      </SwiperSlide>
                    ),
                  )}
                </Swiper>
              </div>

              {/* Thumbnail Slider */}
              <div className="w-full  mt-auto">
                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView={3}
                  freeMode={true}
                  centeredSlides={true}
                  watchSlidesProgress={true}
                  modules={[FreeMode, Navigation, Thumbs]}
                  initialSlide={initialSlideIndex}
                  breakpoints={{
                    360: { slidesPerView: 4 },
                    640: { slidesPerView: 5 },
                    768: { slidesPerView: 7 },
                    1024: { slidesPerView: 10 },
                  }}
                  className="w-full h-full thumbs-swiper"
                >
                  {activeProperty.GalleryImages?.map(
                    (img: any, idx: number) => (
                      <SwiperSlide
                        key={`thumb-${idx}`}
                        className="cursor-pointer opacity-50 transition-opacity [&.swiper-slide-thumb-active]:opacity-100 rounded-lg overflow-hidden border-2 border-transparent [&.swiper-slide-thumb-active]:border-[#C9A962]"
                      >
                        <OptimizedImage
                          src={img.MainGalleryImages}
                          alt={`Thumbnail ${idx}`}
                          sizes="(max-width: 768px) 25vw, 15vw"
                          className="w-full h-full"
                          aspectRatio="1/1"
                        />
                      </SwiperSlide>
                    ),
                  )}
                </Swiper>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
